<template>
    <div class="page content-page" :class="[`content-page--${layout}`]">
        <div class="content-page__shell">
            <div v-if="loading && !page" class="content-page__loading">
                <v-progress-circular indeterminate color="primary" size="32" width="3" />
            </div>

            <template v-else-if="page">
                <header class="content-page__header">
                    <h1 class="content-page__title">{{ page.title }}</h1>
                    <p v-if="page.subtitle" class="content-page__subtitle">{{ page.subtitle }}</p>
                    <p v-if="formattedDate" class="content-page__updated">آخر تحديث: {{ formattedDate }}</p>
                </header>

                <v-expansion-panels
                    v-if="toc.length > 2"
                    v-model="mobileTocOpen"
                    accordion
                    flat
                    class="content-page__toc-mobile"
                >
                    <v-expansion-panel>
                        <v-expansion-panel-header class="content-page__toc-mobile-header">
                            محتويات الصفحة
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <nav aria-label="محتويات الصفحة">
                                <ol class="content-toc__list">
                                    <li v-for="item in toc" :key="item.id">
                                        <a :href="'#' + item.id" @click.prevent="scrollToSection(item.id)">{{ item.text }}</a>
                                    </li>
                                </ol>
                            </nav>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>

                <div class="content-page__layout">
                    <article
                        class="content-page__article"
                        dir="rtl"
                        lang="ar"
                    >
                        <figure v-if="page.heroImage && layout === 'story'" class="content-page__hero">
                            <img :src="page.heroImage" :alt="page.heroImageAlt || page.title" />
                        </figure>

                        <div
                            class="content-prose"
                            :class="{ 'content-prose--story': layout === 'story' }"
                            v-html="renderedHtml"
                        />

                        <section v-if="page.showContact && contact" class="content-contact" aria-labelledby="content-contact-title">
                            <h2 id="content-contact-title" class="content-contact__title">تواصل معنا</h2>
                            <ul class="content-contact__list">
                                <li v-if="contact.email">
                                    <span class="content-contact__label">البريد الإلكتروني</span>
                                    <a :href="'mailto:' + contact.email">{{ contact.email }}</a>
                                </li>
                                <li v-if="contact.whatsapp">
                                    <span class="content-contact__label">واتساب</span>
                                    <a :href="whatsappHref" target="_blank" rel="noopener noreferrer">{{ contact.whatsapp }}</a>
                                </li>
                                <li v-if="contact.website">
                                    <span class="content-contact__label">الموقع</span>
                                    <a :href="contact.websiteHref || ('https://' + contact.website)" target="_blank" rel="noopener noreferrer">{{ displayWebsite }}</a>
                                </li>
                            </ul>
                        </section>
                    </article>

                    <aside v-if="toc.length > 2" class="content-page__toc-side">
                        <nav class="content-toc" aria-label="محتويات الصفحة">
                            <div class="content-toc__title">محتويات الصفحة</div>
                            <ol class="content-toc__list">
                                <li v-for="item in toc" :key="item.id">
                                    <a
                                        :href="'#' + item.id"
                                        :class="{ 'is-active': activeId === item.id }"
                                        @click.prevent="scrollToSection(item.id)"
                                    >
                                        {{ item.text }}
                                    </a>
                                </li>
                            </ol>
                        </nav>
                    </aside>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed'
import { extractToc, formatArabicDate, renderMarkdown } from '@/content/markdown'

export default {
    name: 'ContentPage',
    props: {
        page: {
            type: Object,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            mobileTocOpen: undefined,
            activeId: '',
        };
    },
    computed: {
        ...GlobalComputed,
        layout() {
            return (this.page && this.page.layout) || 'legal';
        },
        toc() {
            if (!this.page || this.page.showToc === false) {
                return [];
            }
            return extractToc(this.page.contentMarkdown);
        },
        renderedHtml() {
            return this.page ? renderMarkdown(this.page.contentMarkdown) : '';
        },
        formattedDate() {
            return this.page ? formatArabicDate(this.page.lastUpdated) : '';
        },
        contact() {
            return this.page && this.page.contact;
        },
        whatsappHref() {
            const phone = String((this.contact && this.contact.whatsapp) || '').replace(/[^\d]/g, '');
            return phone ? `https://wa.me/${phone}` : '#';
        },
        displayWebsite() {
            return String((this.contact && this.contact.website) || '')
                .replace(/^https?:\/\//, '')
                .replace(/\/$/, '');
        },
    },
    watch: {
        renderedHtml() {
            this.$nextTick(this.observeHeadings);
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.observeHeadings();
            this.scrollToHash();
        });
        window.addEventListener('hashchange', this.scrollToHash);
    },
    beforeDestroy() {
        window.removeEventListener('hashchange', this.scrollToHash);
        if (this._observer) {
            this._observer.disconnect();
        }
    },
    methods: {
        scrollToSection(id) {
            const el = document.getElementById(id);
            if (!el) {
                return;
            }
            this.activeId = id;
            this.mobileTocOpen = undefined;
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            if (window.history && window.history.replaceState) {
                window.history.replaceState(null, '', `${this.$route.path}#${id}`);
            }
        },
        scrollToHash() {
            const hash = (this.$route && this.$route.hash || window.location.hash || '').replace('#', '');
            if (hash) {
                this.scrollToSection(hash);
            }
        },
        observeHeadings() {
            if (this._observer) {
                this._observer.disconnect();
            }
            if (typeof IntersectionObserver === 'undefined') {
                return;
            }
            const headings = this.$el.querySelectorAll('.content-prose h2[id]');
            if (!headings.length) {
                return;
            }
            this._observer = new IntersectionObserver((entries) => {
                const visible = entries.find((entry) => entry.isIntersecting);
                if (visible && visible.target.id) {
                    this.activeId = visible.target.id;
                }
            }, {
                rootMargin: '-20% 0px -70% 0px',
                threshold: 0.1,
            });
            headings.forEach((heading) => this._observer.observe(heading));
        },
    },
};
</script>

<style lang="scss">
.content-page {
    --content-max: 920px;
    --content-pad: 24px;
    background: transparent;

    @media (max-width: 959px) {
        --content-pad: 20px;
    }
}

.content-page__shell {
    width: min(var(--content-max), calc(100% - 16px));
    max-width: 100%;
    margin: 0 auto;
    padding: 1.5rem var(--content-pad) 3.5rem;

    @media (min-width: 960px) {
        width: min(1180px, calc(100% - 48px));
        padding: 2.5rem 0 4.5rem;
    }
}

.content-page__loading {
    display: flex;
    justify-content: center;
    padding: 4rem 0;
}

.content-page__header {
    max-width: var(--content-max);
    margin: 0 auto 1.75rem;
    text-align: right;
}

.content-page__title {
    margin: 0;
    font-size: 1.85rem;
    line-height: 1.35;
    font-weight: 800;
    color: inherit;

    @media (min-width: 960px) {
        font-size: 2.15rem;
    }
}

.content-page__subtitle {
    margin: 0.55rem 0 0;
    font-size: 1.05rem;
    opacity: 0.72;
}

.content-page__updated {
    margin: 0.7rem 0 0;
    font-size: 0.92rem;
    opacity: 0.62;
}

.content-page__layout {
    display: block;
    max-width: var(--content-max);
    margin: 0 auto;

    @media (min-width: 960px) {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        gap: 2.5rem;
        max-width: 1180px;
    }
}

.content-page__article {
    min-width: 0;
    flex: 1 1 auto;
    max-width: var(--content-max);
}

.content-page__hero {
    margin: 0 0 1.75rem;
    overflow: hidden;
    border-radius: 18px;

    img {
        display: block;
        width: 100%;
        max-height: 320px;
        object-fit: cover;
        object-position: center 20%;
    }
}

.content-page__toc-side {
    display: none;
}

.content-page__toc-mobile {
    max-width: var(--content-max);
    margin: 0 auto 1.25rem;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 14px !important;
    overflow: hidden;
}

@media (min-width: 960px) {
    .content-page__toc-mobile {
        display: none;
    }

    .content-page__toc-side {
        display: block;
        flex: 0 0 240px;
        position: sticky;
        top: 96px;
    }
}

.content-page__toc-mobile-header {
    font-weight: 700 !important;
    min-height: 48px !important;
}

.content-toc {
    padding: 0.75rem 0 0.75rem 0.25rem;
}

.content-toc__title {
    font-size: 0.82rem;
    font-weight: 800;
    margin-bottom: 0.75rem;
    opacity: 0.7;
}

.content-toc__list {
    list-style: none;
    margin: 0;
    padding: 0;

    li + li {
        margin-top: 0.45rem;
    }

    a {
        display: block;
        color: inherit;
        text-decoration: none;
        font-size: 0.9rem;
        line-height: 1.55;
        opacity: 0.72;
        border-inline-start: 2px solid transparent;
        padding-inline-start: 0.7rem;
        transition: opacity 0.15s ease, border-color 0.15s ease;

        &:hover,
        &:focus {
            opacity: 1;
            outline: none;
        }

        &.is-active {
            opacity: 1;
            font-weight: 700;
            border-inline-start-color: var(--v-primary-base);
        }
    }
}

.content-prose {
    font-size: 16px;
    line-height: 1.9;
    text-align: right;
    overflow-wrap: anywhere;
    word-break: break-word;

    @media (min-width: 960px) {
        font-size: 17.5px;
        line-height: 1.95;
    }

    > *:first-child {
        margin-top: 0;
    }

    p {
        margin: 0 0 1.05em;
    }

    h2,
    h3,
    h4 {
        scroll-margin-top: 92px;
        line-height: 1.4;
        font-weight: 800;
        color: inherit;
    }

    h2 {
        margin: 2.4em 0 0.85em;
        padding-top: 0.35em;
        font-size: 1.28em;
        border-top: 1px solid rgba(0, 0, 0, 0.08);
    }

    h2:first-child {
        border-top: 0;
        margin-top: 0;
        padding-top: 0;
    }

    h3 {
        margin: 1.7em 0 0.65em;
        font-size: 1.08em;
    }

    h4 {
        margin: 1.35em 0 0.5em;
        font-size: 1em;
    }

    ul,
    ol {
        margin: 0 0 1.2em;
        padding: 0 1.25em 0 0;
    }

    li + li {
        margin-top: 0.45em;
    }

    li ul,
    li ol {
        margin: 0.45em 0 0.15em;
    }

    a {
        color: var(--v-primary-base);
        text-decoration: underline;
        text-underline-offset: 0.18em;
        overflow-wrap: anywhere;
    }

    strong {
        font-weight: 800;
    }

    hr {
        border: 0;
        border-top: 1px solid rgba(0, 0, 0, 0.08);
        margin: 2em 0;
    }

    img {
        max-width: 100%;
        height: auto;
        border-radius: 12px;
        display: block;
        margin: 1.25em auto;
    }

    .content-table-wrap {
        width: 100%;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        margin: 0 0 1.2em;
    }

    table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.95em;
        min-width: 420px;
    }

    th,
    td {
        border: 1px solid rgba(0, 0, 0, 0.1);
        padding: 0.65em 0.8em;
        text-align: right;
        white-space: nowrap;
    }

    th {
        font-weight: 800;
        background: rgba(0, 0, 0, 0.03);
    }

    code {
        font-family: "Open Sans", ui-monospace, monospace;
        font-size: 0.88em;
        direction: ltr;
        unicode-bidi: isolate;
        padding: 0.05em 0.3em;
        border-radius: 6px;
        background: rgba(0, 0, 0, 0.05);
    }
}

.content-prose--story > p:first-of-type {
    font-size: 1.04em;
    line-height: 2;
}

.content-callout {
    margin: 1.4em 0;
    padding: 0.95em 1.1em;
    border-radius: 14px;
    background: rgba(25, 118, 210, 0.07);
    border: 1px solid rgba(25, 118, 210, 0.12);
    border-inline-start: 4px solid var(--v-primary-base, #1976d2);

    p {
        margin: 0;
    }

    p + p {
        margin-top: 0.7em;
    }
}

.content-callout--warning {
    background: rgba(237, 108, 2, 0.08);
    border-inline-start-color: #ed6c02;
}

.content-contact {
    margin-top: 2.75rem;
    padding-top: 1.5rem;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.content-contact__title {
    margin: 0 0 1rem;
    font-size: 1.2rem;
    font-weight: 800;
}

.content-contact__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 0.9rem;
}

.content-contact__label {
    display: block;
    font-size: 0.82rem;
    opacity: 0.62;
    margin-bottom: 0.15rem;
}

.content-contact a {
    color: var(--v-primary-base);
    text-decoration: none;
    font-weight: 700;
    overflow-wrap: anywhere;
}

.theme--dark {
    .content-page__toc-mobile,
    .content-prose h2,
    .content-contact {
        border-color: rgba(255, 255, 255, 0.12);
    }

    .content-prose th,
    .content-prose td {
        border-color: rgba(255, 255, 255, 0.12);
    }

    .content-prose th {
        background: rgba(255, 255, 255, 0.04);
    }

    .content-callout {
        background: rgba(255, 255, 255, 0.05);
    }
}
</style>
